
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import Comment, { CommentType } from "./Comment";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

interface CommentSectionProps {
  articleId: string;
}

const CommentSection = ({ articleId }: CommentSectionProps) => {
  const { user } = useAuth();
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState<CommentType[]>([]);

  const handleSubmitComment = () => {
    if (!commentText.trim()) return;
    
    if (!user) {
      toast.error("Please sign in to leave a comment");
      return;
    }

    // In a real app, we would call an API to save the comment
    const newComment: CommentType = {
      id: Date.now().toString(),
      author: user.email || "Anonymous User",
      authorInitials: user.email ? user.email.substring(0, 2).toUpperCase() : "AU",
      content: commentText,
      createdAt: new Date()
    };

    setComments([newComment, ...comments]);
    setCommentText("");
    toast.success("Comment added successfully!");
  };

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Comments ({comments.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <Textarea
            placeholder={user ? "Add your comment..." : "Sign in to comment"}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className="mb-2"
            disabled={!user}
          />
          <Button 
            onClick={handleSubmitComment} 
            disabled={!user || !commentText.trim()}
            className="ml-auto block"
          >
            Post Comment
          </Button>
        </div>

        <div className="space-y-1">
          {comments.map((comment) => (
            <Comment key={comment.id} comment={comment} />
          ))}
        </div>
      </CardContent>
      {comments.length === 0 && (
        <CardFooter className="text-center text-muted-foreground py-8">
          No comments yet. Be the first to share your thoughts!
        </CardFooter>
      )}
    </Card>
  );
};

export default CommentSection;
